(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('gd-prize-gallery')) customElements.define('gd-prize-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const images = this.querySelectorAll('.gd-prize-gallery__image');
      const buttons = this.querySelectorAll('[data-gallery-index]');
      buttons.forEach(button => button.addEventListener('click', () => {
        const index = Number(button.dataset.galleryIndex);
        images.forEach((image, i) => { image.hidden = i !== index; });
        buttons.forEach((tab, i) => tab.setAttribute('aria-pressed', String(i === index)));
      }, { signal: this.abort.signal }));
    }
    disconnectedCallback() { this.abort?.abort(); }
  });

  if (!customElements.get('gd-recently-viewed')) customElements.define('gd-recently-viewed', class extends HTMLElement {
    connectedCallback() {
      const key = 'gemdraw:recent-products:v1';
      try {
        const saved = JSON.parse(localStorage.getItem(key) || '[]');
        const history = Array.isArray(saved) ? saved : [];
        const currentData = document.querySelector('[data-sample-recent-product]')?.textContent || this.querySelector('[data-recent-product]')?.textContent;
        const current = currentData ? JSON.parse(currentData) : null;
        const allowPreview = this.dataset.previewEnabled === 'true';
        const usable = history.filter(item => item && typeof item.url === 'string' && typeof item.image === 'string' && (!item.sample || allowPreview));
        const previous = usable.filter(item => String(item.id) !== String(current?.id || this.dataset.currentId)).slice(0, 6);
        const list = this.querySelector('[data-recent-list]');
        for (const item of previous) {
          const url = new URL(item.url, location.origin);
          const imageUrl = new URL(item.image, location.origin);
          if (url.origin !== location.origin || !/^https?:$/.test(imageUrl.protocol)) continue;
          const li = document.createElement('li');
          const link = document.createElement('a'); link.href = url.href;
          const img = document.createElement('img'); img.src = imageUrl.href; img.alt = item.name; img.width = 400; img.height = 400; img.loading = 'lazy';
          const title = document.createElement('h3'); title.textContent = item.name;
          const price = document.createElement('p'); price.textContent = item.sample ? `${item.price} · Sample product` : item.price;
          link.append(img);
          if (item.entries) {
            const badge = document.createElement('span'); badge.className = 'gd-entry-badge gd-entry-badge--bar';
            const row = document.createElement('span'); row.className = 'gd-entry-badge__row';
            const entries = document.createElement('span'); entries.className = 'gd-entry-badge__entries';
            const value = document.createElement('span'); value.className = 'gd-entry-badge__value'; value.textContent = item.entries;
            const unit = document.createElement('span'); unit.className = 'gd-entry-badge__unit'; unit.textContent = 'Entries';
            entries.append(value, unit); row.append(entries);
            if (item.multiplier) { const multiplier = document.createElement('span'); multiplier.className = 'gd-entry-badge__multiplier'; multiplier.textContent = item.multiplier; row.append(multiplier); }
            badge.append(row); link.append(badge);
          }
          link.append(title, price); li.append(link); list.append(li);
        }
        this.hidden = list.children.length === 0;
        if (current && (!current.sample || allowPreview)) localStorage.setItem(key, JSON.stringify([current, ...usable.filter(item => item.id !== current.id)].slice(0, 8)));
      } catch (_) { /* Browsing history is optional when browser storage is unavailable. */ }
    }
  });

  if (!customElements.get('gd-scroll-rail')) customElements.define('gd-scroll-rail', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-rail]');
      if (!this.rail) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const prev = this.querySelector('[data-rail-prev]');
      const next = this.querySelector('[data-rail-next]');
      const controls = this.querySelector('[data-rail-controls]');
      const update = () => {
        const max = this.rail.scrollWidth - this.rail.clientWidth;
        if (controls) controls.hidden = max < 4;
        if (prev) prev.disabled = this.rail.scrollLeft < 4;
        if (next) next.disabled = this.rail.scrollLeft >= max - 4;
        const status = this.querySelector('[data-rail-status]');
        if (status) status.textContent = `${Math.min(this.rail.children.length, Math.round(this.rail.scrollLeft / Math.max(1, this.rail.firstElementChild?.getBoundingClientRect().width + 20)) + 1)} / ${this.rail.children.length}`;
      };
      const move = direction => this.rail.scrollBy({ left: direction * (this.rail.firstElementChild.getBoundingClientRect().width + 20), behavior: reduced() ? 'instant' : 'smooth' });
      prev?.addEventListener('click', () => move(-1), options);
      next?.addEventListener('click', () => move(1), options);
      this.rail.addEventListener('scroll', update, options);
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this.resize = new ResizeObserver(update); this.resize.observe(this.rail); update();
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
  });

  if (!customElements.get('gd-prize-viewer')) customElements.define('gd-prize-viewer', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      const stage = this.querySelector('[data-prize-stage]');
      const object = this.querySelector('[data-prize-object]');
      const model = this.querySelector('model-viewer');
      let angle = 0, start = null;
      const draw = () => {
        if (object) object.style.transform = `rotateY(${Math.max(-35, Math.min(35, angle))}deg) rotateX(4deg)`;
        if (model) model.setAttribute('camera-orbit', `${angle}deg 75deg auto`);
      };
      this.querySelectorAll('[data-prize-turn]').forEach(button => button.addEventListener('click', () => { angle += Number(button.dataset.prizeTurn) * 15; draw(); }, opts));
      this.querySelector('[data-prize-reset]')?.addEventListener('click', () => { angle = 0; if (object) object.style.transform = ''; if (model) model.setAttribute('camera-orbit', '0deg 75deg auto'); }, opts);
      if (object) {
        stage.addEventListener('pointerdown', event => { if (event.button !== 0) return; start = { x: event.clientX, angle }; stage.setPointerCapture(event.pointerId); }, opts);
        stage.addEventListener('pointermove', event => { if (!start) return; angle = Math.max(-35, Math.min(35, start.angle + (event.clientX - start.x) / 4)); draw(); }, opts);
        for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) stage.addEventListener(type, () => { start = null; }, opts);
      }
      if (model) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.observer.disconnect();
          if (window.Shopify?.loadFeatures) Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: errors => { if (!errors && this.isConnected && Shopify.ModelViewerUI) this.viewer = new Shopify.ModelViewerUI(model); } }]);
        }, { rootMargin: '200px' }); this.observer.observe(this);
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.viewer?.destroy?.(); }
  });

  if (!customElements.get('gd-product-preview')) customElements.define('gd-product-preview', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      const dialog = this.querySelector('dialog');
      if (!dialog) return;
      const open = trigger => {
        this.trigger = dialog.open ? this.trigger : trigger;
        const data = trigger.dataset;
        this.querySelector('[data-preview-title]').textContent = data.previewName;
        this.querySelector('[data-preview-image]').src = data.previewImage;
        this.querySelector('[data-preview-image]').alt = `${data.previewName} — sample product`;
        this.querySelector('[data-preview-category]').textContent = data.previewCategory || 'GEMDRAW';
        this.querySelector('[data-preview-price]').textContent = data.previewPrice || 'Price to be confirmed';
        this.querySelector('[data-preview-entries]').textContent = data.previewEntries || '—';
        this.querySelector('[data-preview-multiplier]').textContent = data.previewMultiplier || '—';
        this.querySelector('[data-preview-quantity]').value = '1';
        this.querySelectorAll('[data-preview-size]').forEach(button => button.setAttribute('aria-pressed', String(button.textContent === 'M')));
        this.querySelector('[data-preview-sizes]').hidden = data.previewCategory !== 'Apparel';
        this.querySelector('[data-preview-status]').textContent = 'Sample product · Not available for purchase';
        if (!dialog.open) dialog.showModal();
        document.body.classList.add('gd-preview-open'); dialog.scrollTop = 0;
        this.querySelector('[data-preview-title]').focus({ preventScroll: true });
      };
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-product-preview]');
        if (!trigger) return; event.preventDefault(); open(trigger);
      }, opts);
      this.querySelector('[data-preview-close]').addEventListener('click', () => dialog.close(), opts);
      dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } }, opts);
      dialog.addEventListener('close', () => { document.body.classList.remove('gd-preview-open'); this.trigger?.focus(); }, opts);
      this.querySelectorAll('[data-preview-size]').forEach(button => button.addEventListener('click', () => {
        this.querySelectorAll('[data-preview-size]').forEach(option => option.setAttribute('aria-pressed', String(option === button)));
        this.querySelector('[data-preview-status]').textContent = `Size ${button.textContent} selected · Preview only`;
      }, opts));
      this.querySelector('[data-preview-quantity]').addEventListener('change', event => { event.target.value = String(Math.min(10, Math.max(1, Math.floor(Number(event.target.value) || 1)))); }, opts);
    }
    disconnectedCallback() { this.abort?.abort(); document.body.classList.remove('gd-preview-open'); }
  });
})();
